import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuthToken } from '../../../auth/tokenvalidation';
import { checkClarificationHasUnread } from './fetchClarificationResponds';

const useClarificationHasUnread = ({
  procurementId = null,
  procurementMitraId = null,
  clauseDetailIds = [],
  isEvaluatorView = true,
  enabled = true,
  includeClause = true,
  includeTypeResponse = true,
} = {}) => {
  const [hasUnread, setHasUnread] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const clauseDetailIdsKey = useMemo(
    () =>
      [...new Set((clauseDetailIds ?? []).filter(Boolean))]
        .map(String)
        .sort()
        .join(','),
    [clauseDetailIds]
  );

  const clearUnread = useCallback(() => setHasUnread(false), []);

  useEffect(() => {
    if (!enabled || !procurementId) {
      setHasUnread(false);
      setIsChecking(false);
      return undefined;
    }

    if (includeClause && !procurementMitraId) {
      setHasUnread(false);
      setIsChecking(false);
      return undefined;
    }

    let cancelled = false;

    const run = async () => {
      const token = getAuthToken();
      if (!token) {
        if (!cancelled) setHasUnread(false);
        return;
      }

      setIsChecking(true);
      try {
        const clauseIds = clauseDetailIdsKey
          ? clauseDetailIdsKey.split(',').filter(Boolean)
          : [];
        const next = await checkClarificationHasUnread({
          token,
          procurementId,
          procurementMitraId,
          clauseDetailIds: clauseIds,
          isEvaluatorView,
          includeClause,
          includeTypeResponse,
          allowUnscoped: true,
        });
        if (!cancelled) setHasUnread(next);
      } catch {
        if (!cancelled) setHasUnread(false);
      } finally {
        if (!cancelled) setIsChecking(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    clauseDetailIdsKey,
    enabled,
    includeClause,
    includeTypeResponse,
    isEvaluatorView,
    procurementId,
    procurementMitraId,
  ]);

  return { hasUnread, isChecking, clearUnread, setHasUnread };
};

export default useClarificationHasUnread;
